import {
  ActionIcon,
  Alert,
  Button,
  Collapse,
  Group,
  List,
  Modal,
  Skeleton,
  Stack,
  Tooltip
} from "@mantine/core";
import {
  IconCaretDown,
  IconCaretUp,
  IconCopy,
  IconRefresh,
  IconTrash
} from "@tabler/icons-react";
import { useState } from "react";
import api from "../../../api";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { AlertPresets } from "../../../mantine/mantinePresets";
import urls from "../../../urls";
import { copyToClipboard } from "../../../utils/copyToClipboard";
import { AnchorLink } from "../../AnchorLink/AnchorLink";
import {
  type TodoCalendarIntegration as TodoCalendarIntegrationType,
  useTodoCalendarIntegrations
} from "./useTodoCalenderIntegrations";

interface TodoCalendarIntegrationProps {
  variantId: number;
}

function TodoCalendarIntegration({ variantId }: TodoCalendarIntegrationProps) {
  const [isModalOpen, setModelOpen] = useState(false);
  const toggleModal = () => setModelOpen(!isModalOpen);

  return (
    <>
      <Button color="neutral" variant="outline" onClick={toggleModal}>
        Kalenderintegration
      </Button>
      <TodoCalendarIntegrationModal
        isModalOpen={isModalOpen}
        variantId={variantId}
        onToggleModal={toggleModal}
      />
    </>
  );
}

interface TodoCalendarIntegrationModalProps {
  onToggleModal: () => void;
  isModalOpen: boolean;
  variantId: number;
}

function TodoCalendarIntegrationModal({
  onToggleModal,
  isModalOpen,
  variantId
}: TodoCalendarIntegrationModalProps) {
  return (
    <Modal
      className="TodoCalendarIntegrationModal"
      opened={isModalOpen}
      size="xl"
      title="Kalenderintegration"
      onClose={onToggleModal}
    >
      <Stack>
        Sie haben die Möglichkeit durch einen Link die Aufgaben direkt mit Ihrem
        Kalender (Outlook, Google Calendar, etc.) zu synchronisieren. Dabei
        werden nur Aufgaben mit Fälligkeitsdatum berücksichtigt.
        <Alert color={AlertPresets.warning.color}>
          Wir empfehlen Ihnen den Link genauso vertraulich zu behandeln wie Ihr
          Passwort, denn jede Person mit Kenntnis des Links hat auch Zugriff auf
          die Kalendersynchronisation.
        </Alert>
        <TodoCalendarIntegrationController variantId={variantId} />
        <TodoCalendarIntegrationTutorial />
      </Stack>
      <ModalFooter>
        <Button variant="default" onClick={onToggleModal}>
          Schließen
        </Button>
      </ModalFooter>
    </Modal>
  );
}

interface TodoCalendarIntegrationControllerProps {
  variantId: number;
}

function TodoCalendarIntegrationController({
  variantId
}: TodoCalendarIntegrationControllerProps) {
  const {
    data: calendarIntegrations,
    refetch,
    isLoading,
    error
  } = useTodoCalendarIntegrations(variantId);

  const createEntry = async () => {
    await api.post(urls.api.todoCalendarIntegrations(variantId), {
      variant: variantId
    });
    refetch();
  };

  const deleteEntry = async (calendarIntegrationId: number) => {
    await api.delete(
      urls.api.todoCalendarIntegrationDetail(calendarIntegrationId)
    );
    refetch();
  };

  const refreshEntry = async (calendarIntegrationId: number) => {
    await api.post(
      urls.api.todoCalendarIntegrationRefresh(calendarIntegrationId)
    );
    refetch();
  };

  if (isLoading) {
    return <Skeleton height={45} mt={"1rem"} />;
  }

  if (error) {
    return <Alert color={AlertPresets.error.color}>{error.message}</Alert>;
  }

  if (!calendarIntegrations) {
    return (
      <Alert color={AlertPresets.error.color}>
        Keine Kalenderintegrationen gefunden
      </Alert>
    );
  }

  if (calendarIntegrations.length === 0) {
    return <ConfirmTodoCalendarIntegrationCreate onCreate={createEntry} />;
  } else {
    return (
      <TodoCalendarIntegrationEntry
        calendarIntegration={calendarIntegrations[0]}
        onDelete={deleteEntry}
        onRefresh={refreshEntry}
      />
    );
  }
}

function TodoCalendarIntegrationTutorial() {
  const [isTutorialOpen, setTutorialOpen] = useState(false);
  const toggleTutorial = () => setTutorialOpen(!isTutorialOpen);
  const caret = isTutorialOpen ? (
    <IconCaretUp strokeWidth={1} />
  ) : (
    <IconCaretDown strokeWidth={1} />
  );

  return (
    <div className="TodoCalendarIntegrationTutorial">
      <Group align="center" gap="xs" onClick={toggleTutorial}>
        {caret} Anleitung zur Einrichtung im Kalender
      </Group>
      <Collapse expanded={isTutorialOpen}>
        <List mt="1rem" withPadding>
          <List.Item>
            <AnchorLink href="https://support.office.com/de-de/article/video-importieren-von-kalendern-8e8364e1-400e-4c0f-a573-fe76b5a2d379">
              Office 365
            </AnchorLink>
            : siehe &quot;Hinzufügen von Internetkalendern&quot;
          </List.Item>
          <List.Item>
            <AnchorLink href="https://support.office.com/de-de/article/importieren-oder-abonnieren-eines-kalenders-in-outlook-com-cff1429c-5af6-41ec-a5b4-74f2c278e98c">
              Outlook.com
            </AnchorLink>
            : siehe &quot;Abonnieren eines Kalenders&quot;
          </List.Item>
          <List.Item>
            Outlook: In der Offline-Applikation von Outlook ist es nicht möglich
            den Kalender zu importieren. Wenn Sie den Import in Office 365 bzw.
            Outlook.com einrichten, wird es aber dennoch mit der
            Offline-Applikation synchronisiert.
          </List.Item>
          <List.Item>
            <AnchorLink href="https://support.google.com/calendar/answer/37648?hl=de">
              Google Calendar
            </AnchorLink>
            : siehe &quot;Kalender abrufen&quot;
          </List.Item>
          <List.Item>
            <AnchorLink href="https://support.apple.com/de-de/HT202361">
              iCal
            </AnchorLink>
            : siehe &quot;Ein neues iCloud-Kalenderabonnement auf dem Mac
            einrichten&quot;
          </List.Item>
          <List.Item>
            Für weitere Kalenderapplikationen wenden Sie sich bitte an Ihren
            IT-Support
          </List.Item>
        </List>
      </Collapse>
    </div>
  );
}

interface ConfirmTodoCalendarIntegrationCreateProps {
  onCreate: () => void;
}

function ConfirmTodoCalendarIntegrationCreate({
  onCreate
}: ConfirmTodoCalendarIntegrationCreateProps) {
  return (
    <div>
      <p>
        Mit dem Klick auf „Link erstellen“ bestätigen Sie, dass Sie über die
        Sicherheitsimplikationen informiert wurden. Nach dem Erstellen können
        Sie den Link auch aktualisieren oder wieder löschen.
      </p>
      <Button onClick={onCreate}>Link erstellen</Button>
    </div>
  );
}

interface TodoCalendarIntegrationEntryProps {
  calendarIntegration: TodoCalendarIntegrationType;
  onDelete: (calendarIntegrationId: number) => void;
  onRefresh: (calendarIntegrationId: number) => void;
}

function TodoCalendarIntegrationEntry({
  calendarIntegration,
  onDelete,
  onRefresh
}: TodoCalendarIntegrationEntryProps) {
  const [linkCopied, setLinkCopied] = useState(false);
  return (
    <Group
      bd="1px solid var(--mantine-color-gray-2)"
      className="TodoCalendarIntegrationEntry"
      justify="space-between"
      my="1rem"
      p="sm"
    >
      <div className="todo-calendar-url">
        {calendarIntegration.calendar_url}
      </div>
      <Group gap="xs">
        <Tooltip label={linkCopied ? "Link wurde kopiert" : "Link kopieren"}>
          <ActionIcon
            color="info"
            size="xs"
            variant="transparent"
            onClick={() => {
              copyToClipboard(calendarIntegration.calendar_url);
              setLinkCopied(true);
            }}
            onMouseOut={() => setLinkCopied(false)}
          >
            <IconCopy />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Link erneuern">
          <ActionIcon
            color="info"
            size="xs"
            variant="transparent"
            onClick={() => onRefresh(calendarIntegration.id)}
          >
            <IconRefresh />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Link löschen">
          <ActionIcon
            color="error"
            size="xs"
            variant="transparent"
            onClick={() => onDelete(calendarIntegration.id)}
          >
            <IconTrash />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Group>
  );
}

export { TodoCalendarIntegration };
